/**
 * useNewItemTracker — quietly tracks which IDs are "new" in this render
 * relative to the previous render. Returns a Set of newly arrived ids
 * that the consumer can apply a 4-second settle-in treatment to.
 *
 * Behaviour:
 *  - First render: NO ids are flagged as new (we don't theatrically settle
 *    everything on initial mount — items show via their parent's normal
 *    enter animation).
 *  - Subsequent renders: only ids that didn't exist last render are flagged.
 *  - Once flagged, an id remains in `newIds` for `holdMs` (default 4200ms)
 *    so the CSS animation can complete, then it's silently retired.
 *
 * Usage:
 *   const newIds = useNewItemTracker(items.map(i => i.id));
 *   ...
 *   <li className={newIds.has(item.id) ? "cf-settle" : ""} />
 */
import { useEffect, useRef, useState } from "react";

export function useNewItemTracker(ids, holdMs = 4200) {
  const seenRef = useRef(null); // null = pre-mount (first render)
  const [newIds, setNewIds] = useState(() => new Set());

  useEffect(() => {
    const incoming = new Set(ids || []);
    if (seenRef.current === null) {
      // First render — accept the baseline silently.
      seenRef.current = incoming;
      return undefined;
    }
    const fresh = new Set();
    for (const id of incoming) {
      if (!seenRef.current.has(id)) fresh.add(id);
    }
    seenRef.current = incoming;
    if (fresh.size === 0) return undefined;
    setNewIds((prev) => {
      const next = new Set(prev);
      fresh.forEach((id) => next.add(id));
      return next;
    });
    const timer = setTimeout(() => {
      setNewIds((prev) => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        fresh.forEach((id) => next.delete(id));
        return next;
      });
    }, holdMs);
    return () => clearTimeout(timer);
    // We deliberately depend on a stable join so React batches correctly
    // even if `ids` is a new array reference each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.isArray(ids) ? ids.join("|") : ""]);

  return newIds;
}
